import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CircleNotch, MagnifyingGlass } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { useCurrency } from '../../contexts/CompanyContext';
import { ChartCard, TrendBarChart, DonutChart } from './FinanceCharts';
import { DataNote } from './DataNote';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERIES = {
  income: [{ key: 'income', label: 'Income', color: '#059669' }],
  expense: [{ key: 'expense', label: 'Expenses', color: '#dc2626' }],
  asset: [{ key: 'asset', label: 'Assets movement', color: '#2563eb' }],
  liability: [{ key: 'liability', label: 'Liabilities movement', color: '#dc2626' }],
  equity: [{ key: 'equity', label: 'Equity movement', color: '#7c3aed' }],
  pnl: [{ key: 'income', label: 'Income', color: '#059669' }, { key: 'expense', label: 'Expenses', color: '#dc2626' }],
  cash: [{ key: 'asset', label: 'Net cash movement', color: '#0891b2' }],
  equity_re: [{ key: 'equity', label: 'Equity', color: '#7c3aed' }, { key: 'income', label: 'Income', color: '#059669' }, { key: 'expense', label: 'Expenses', color: '#dc2626' }],
};

const STATUS_STYLE = {
  paid: 'bg-emerald-500/10 text-emerald-700 border-emerald-500/30',
  partial: 'bg-amber-500/10 text-amber-700 border-amber-500/30',
  unpaid: 'bg-rose-500/10 text-rose-700 border-rose-500/30',
};

function payState(doc) {
  if (!doc || doc.balance == null) return null;
  if (doc.balance <= 0.01) return 'paid';
  return doc.paid > 0.01 ? 'partial' : 'unpaid';
}

export const EXPLAIN = {
  income: 'Revenue = credits − debits on all Income accounts in the period (amounts before VAT, net of discounts and credit notes). Each row is the journal line posted when an invoice or income voucher was approved.',
  expense: 'Expenses = debits − credits on all Expense accounts in the period (before VAT). Each row is the journal line posted by a bill, payment voucher, payroll or manual journal.',
  pnl: 'Net Profit = Revenue − Expenses for the period. Income rows add, expense rows subtract (shown in red).',
  asset: 'Assets = debits − credits on Asset accounts up to the date (cash, bank, receivables, inventory, fixed assets).',
  liability: 'Liabilities = credits − debits on Liability accounts up to the date (payables, VAT payable, loans).',
  equity: 'Equity = credits − debits on Equity accounts up to the date.',
  equity_re: 'Equity = capital & equity accounts + retained earnings (all income − all expenses) up to the date.',
  cash: 'Cash & Bank = debits (money in) − credits (money out) on Cash and Bank accounts. Each row is a receipt, payment or transfer entry.',
};

// request: { category, accountId?, startDate?, endDate?, title, explain? }
export function DrilldownDialog({ request, onClose }) {
  const { formatCurrency } = useCurrency();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');

  useEffect(() => {
    if (!request) return;
    setData(null);
    setQ('');
    setLoading(true);
    const params = new URLSearchParams({ category: request.category });
    if (request.accountId) params.append('account_id', request.accountId);
    if (request.startDate) params.append('start_date', request.startDate);
    if (request.endDate) params.append('end_date', request.endDate);
    axios.get(`${API_URL}/reports/drilldown?${params}`)
      .then((r) => setData(r.data))
      .catch(() => toast.error('Failed to load details'))
      .finally(() => setLoading(false));
  }, [request]);

  const rows = useMemo(() => {
    const list = data?.transactions || [];
    const s = q.trim().toLowerCase();
    if (!s) return list;
    return list.filter((t) => [t.reference, t.description, t.account_name, t.document?.doc_number, t.document?.party]
      .some((v) => (v || '').toString().toLowerCase().includes(s)));
  }, [data, q]);

  const filteredTotal = rows.reduce((s, t) => s + (t.amount || 0), 0);
  const series = SERIES[request?.category] || SERIES.asset;
  const breakdown = (data?.accounts?.length > 1
    ? data.accounts.map((a) => ({ name: `${a.account_code} ${a.account_name}`, value: a.amount }))
    : (data?.parties || []).map((p) => ({ name: p.party, value: p.amount })));
  const period = request?.startDate ? `${request.startDate} → ${request.endDate || 'today'}` : `As of ${request?.endDate || 'today'}`;

  return (
    <Dialog open={!!request} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto [&>*]:min-w-0" data-testid="drilldown-dialog">
        <DialogHeader>
          <DialogTitle data-testid="drilldown-title">{request?.title}</DialogTitle>
          <DialogDescription>{period} · every posted entry behind this figure, with its source document</DialogDescription>
        </DialogHeader>

        <p className="text-xs rounded-md bg-muted/40 border px-3 py-2" data-testid="drilldown-explain">
          <span className="font-semibold">How it's calculated: </span>{request?.explain || EXPLAIN[request?.category]}
        </p>
        <DataNote />

        {loading || !data ? (
          <div className="flex items-center justify-center h-48" data-testid="drilldown-loading">
            <CircleNotch className="animate-spin text-primary" size={28} />
          </div>
        ) : (
          <div className="space-y-4 min-w-0">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Stat label="Total" value={formatCurrency(data.total)} testid="drilldown-total" strong />
              <Stat label="Entries" value={data.count} testid="drilldown-count" />
              <Stat label="Accounts" value={data.accounts.length} testid="drilldown-accounts" />
              <Stat label="Documents linked" value={data.transactions.filter((t) => t.document).length} testid="drilldown-docs" />
            </div>

            <div className="grid md:grid-cols-2 gap-4 [&>*]:min-w-0">
              <ChartCard title="Monthly trend" testid="drilldown-trend-chart">
                <TrendBarChart data={data.monthly} series={series} height={200} />
              </ChartCard>
              <ChartCard title={data.accounts.length > 1 ? 'By account' : 'By customer / supplier'} testid="drilldown-breakdown-chart">
                <DonutChart data={breakdown} height={200} />
              </ChartCard>
            </div>

            <div className="flex items-center gap-3">
              <div className="relative flex-1 max-w-sm">
                <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search document, party, account…" className="pl-8 h-9" data-testid="drilldown-search" />
              </div>
              <span className="text-xs text-muted-foreground" data-testid="drilldown-filtered-total">
                {rows.length} row(s) · {formatCurrency(filteredTotal)}
              </span>
            </div>

            <DrillTable rows={rows} formatCurrency={formatCurrency} />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value, testid, strong }) {
  return (
    <div className={`rounded-lg border p-3 ${strong ? 'bg-primary/5 border-primary/30' : 'bg-card'}`}>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`mt-1 font-semibold ${strong ? 'text-lg' : 'text-base'}`} data-testid={testid}>{value}</div>
    </div>
  );
}

function DrillTable({ rows, formatCurrency }) {
  const fmt = (v) => (v == null ? '—' : formatCurrency(v));
  return (
    <div className="rounded-lg border overflow-x-auto max-h-[420px] overflow-y-auto">
      <table className="w-full text-xs" data-testid="drilldown-table">
        <thead className="bg-muted/50 uppercase tracking-wide text-muted-foreground sticky top-0">
          <tr>
            {['Date', 'Document', 'Party', 'Account', 'Before VAT', 'VAT', 'Doc Total', 'Paid', 'Balance', 'Status', 'Amount'].map((h, i) => (
              <th key={h} className={`px-2.5 py-2 whitespace-nowrap ${i >= 4 && h !== 'Status' ? 'text-right' : 'text-left'}`}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr><td colSpan={11} className="px-3 py-8 text-center text-muted-foreground">No entries found</td></tr>
          )}
          {rows.map((t, i) => {
            const d = t.document;
            const st = payState(d);
            return (
              <tr key={`${t.entry_id}-${t.account_id}-${i}`} className="border-t hover:bg-muted/30" data-testid={`drilldown-row-${i}`}>
                <td className="px-2.5 py-2 whitespace-nowrap">{t.date}</td>
                <td className="px-2.5 py-2 whitespace-nowrap">
                  <div className="font-mono font-medium">{d?.doc_number || t.reference || `JE-${t.entry_number}`}</div>
                  <div className="text-[10px] text-muted-foreground capitalize">{d ? d.doc_type : 'journal'}</div>
                </td>
                <td className="px-2.5 py-2 max-w-[160px] truncate" title={d?.party || t.description}>{d?.party || <span className="text-muted-foreground">{t.description}</span>}</td>
                <td className="px-2.5 py-2 whitespace-nowrap"><span className="font-mono text-muted-foreground">{t.account_code}</span> {t.account_name}</td>
                <td className="px-2.5 py-2 text-right font-mono">{fmt(d?.before_vat)}</td>
                <td className="px-2.5 py-2 text-right font-mono">{fmt(d?.vat)}</td>
                <td className="px-2.5 py-2 text-right font-mono">{fmt(d?.doc_total)}</td>
                <td className="px-2.5 py-2 text-right font-mono">{fmt(d?.paid)}</td>
                <td className="px-2.5 py-2 text-right font-mono">{fmt(d?.balance)}</td>
                <td className="px-2.5 py-2">
                  {st ? <Badge variant="outline" className={`text-[10px] capitalize ${STATUS_STYLE[st]}`}>{st}</Badge>
                    : d?.status ? <Badge variant="outline" className="text-[10px] capitalize">{d.status}</Badge> : '—'}
                </td>
                <td className={`px-2.5 py-2 text-right font-mono font-semibold ${t.amount < 0 ? 'text-red-600' : ''}`}>{formatCurrency(t.amount)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
